/**
 * Cashflow Cockpit — lens grouping & sorting (ported from the HTML's grpKey/render).
 */

import {
	AGING_GROUP_ORDER,
	AGING_LABEL,
	LABEL_MAP,
	LABEL_ORDER,
	OUTREACH,
	RISK_LABEL,
	VTIER_LABEL,
} from './constants';
import { InvoiceRecord, Lens } from './types';

/** The group key an invoice falls under, for the active lens. */
export function groupKey(r: InvoiceRecord, lens: Lens): string {
	switch (lens) {
		case 'client':
			return r.cust || '—';
		case 'kam':
			return r.kam || r.accts?.[0] || '—';
		case 'month':
			return r.month || '—';
		case 'aging':
			return (r.aging && AGING_LABEL[r.aging]) || r.aging || '—';
		case 'risk':
			return (r.risk && RISK_LABEL[r.risk]) || r.risk || '—';
		case 'verify':
			return (r.vtier_inv && VTIER_LABEL[r.vtier_inv]) || '—';
		case 'tactics':
			return r.forensic
				? 'audited'
				: 'not audited yet';
		case 'outreach': {
			const o = OUTREACH[r.ostate];
			return o ? o[0] : r.ostate || '—';
		}
		case 'state':
		default: {
			for (const l of LABEL_ORDER) if (r.labels.includes(l)) return LABEL_MAP[l][0];
			return r.labels[0] || '—';
		}
	}
}

/** Ordered group keys for a lens: aging worst-first, month newest-first, else by total ₹ desc. */
export function orderedGroupKeys(
	groups: Record<string, InvoiceRecord[]>,
	lens: Lens
): string[] {
	const keys = Object.keys(groups);
	if (lens === 'aging') {
		return keys.sort(
			(a, b) =>
				(AGING_GROUP_ORDER.indexOf(a) + 1 || 99) - (AGING_GROUP_ORDER.indexOf(b) + 1 || 99)
		);
	}
	if (lens === 'month') {
		return keys.sort().reverse();
	}
	const total = (k: string) => groups[k].reduce((s, r) => s + (r.amt || 0), 0);
	return keys.sort((a, b) => total(b) - total(a));
}

export type SortKey = 'num' | 'cust' | 'amt' | 'tier' | 'emails' | 'quiet';

/** Sort invoices within a group by the active column. */
export function sortInvoices(
	rows: InvoiceRecord[],
	key: SortKey,
	dir: 1 | -1
): InvoiceRecord[] {
	const val = (r: InvoiceRecord): string | number => {
		switch (key) {
			case 'emails':
				return r.c?.emails ?? -Infinity;
			case 'quiet':
				return r.c?.quiet ?? -Infinity;
			case 'amt':
				return r.amt ?? -Infinity;
			case 'num':
				return r.num;
			case 'cust':
				return r.cust ?? '';
			case 'tier':
				return r.tier ?? '';
		}
	};
	return [...rows].sort((a, b) => {
		const av = val(a);
		const bv = val(b);
		return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
	});
}
